<template>
  <div class="automation-progress-ring" :style="ringStyle" aria-hidden="true">
    <svg class="automation-progress-ring__svg" :width="size" :height="size" viewBox="0 0 100 100">
      <circle
        class="automation-progress-ring__track"
        cx="50"
        cy="50"
        :r="radius"
        :stroke-width="normalizedStrokeWidth" />
      <path
        v-if="ratio !== null && ratio > 0"
        class="automation-progress-ring__progress"
        :d="arcPath"
        :stroke-width="normalizedStrokeWidth" />
      <!-- Dot at the tip of the arc -->
      <circle
        v-if="ratio !== null && ratio > 0 && ratio < 1"
        class="automation-progress-ring__tip-shadow"
        :cx="tipPoint.x"
        :cy="tipPoint.y"
        :r="normalizedStrokeWidth * 0.68" />
      <circle
        v-if="ratio !== null && ratio > 0 && ratio < 1"
        class="automation-progress-ring__tip"
        :cx="tipPoint.x"
        :cy="tipPoint.y"
        :r="normalizedStrokeWidth * 0.46" />
    </svg>
    <span v-if="controlIcon" class="automation-progress-ring__glyph">
      <ControlIcon :name="controlIcon" size="18px" />
    </span>
    <span v-else-if="icon" class="material-symbols-outlined automation-progress-ring__icon">{{ icon }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';

interface Props {
  ratio: number | null;
  icon?: string;
  /**
   * A `ControlIcon` name; when set, the ring's middle draws the sheet's drawn
   * glyph instead of the Material `icon`. Control surfaces (the sheet's
   * automation panel) pass this — the tile indicators keep `icon` (P11 sweep,
   * card t_bd3aea71).
   */
  controlIcon?: string;
  size?: number;
  strokeWidth?: number;
  color?: string;
  trackColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 42,
  strokeWidth: 4,
  icon: '',
  controlIcon: '',
  color: 'rgb(16, 185, 129)',
  trackColor: 'rgba(148, 163, 184, 0.24)',
});

const radius = computed(() => 50 - props.strokeWidth * 2.5);
const normalizedStrokeWidth = computed(() => props.strokeWidth * 2.4);

const ringStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  '--automation-ring-color': props.color,
  '--automation-ring-track': props.trackColor,
}));

/**
 * Build a clockwise arc path starting at south (0:00 position).
 * ratio=1 → full circle, ratio=0.5 → half circle (south → north → south), etc.
 * South-origin: angle = ratio * 2π + π/2
 */
const arcPath = computed(() => {
  if (props.ratio === null || props.ratio <= 0) return '';
  const r = radius.value;
  const clamped = Math.min(1, Math.max(0, props.ratio));

  // South point (start of arc, ratio=0)
  const sx = 50;
  const sy = 50 + r;

  if (clamped >= 1.0) {
    // Full circle: two half-arcs to avoid degenerate same-point case
    return `M ${sx} ${sy} A ${r} ${r} 0 0 1 ${50} ${50 - r} A ${r} ${r} 0 0 1 ${sx} ${sy}`;
  }

  const angle = clamped * 2 * Math.PI + Math.PI / 2;
  const ex = 50 + r * Math.cos(angle);
  const ey = 50 + r * Math.sin(angle);
  const largeArc = clamped > 0.5 ? 1 : 0;
  return `M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`;
});

const tipPoint = computed(() => {
  if (props.ratio === null) return { x: 50, y: 50 + radius.value };
  const r = radius.value;
  const clamped = Math.min(1, Math.max(0, props.ratio));
  const angle = clamped * 2 * Math.PI + Math.PI / 2;
  return { x: 50 + r * Math.cos(angle), y: 50 + r * Math.sin(angle) };
});
</script>

<style scoped>
.automation-progress-ring {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.automation-progress-ring__svg {
  overflow: visible;
}

.automation-progress-ring__track,
.automation-progress-ring__progress {
  fill: none;
}

.automation-progress-ring__track {
  stroke: var(--automation-ring-track);
}

.automation-progress-ring__progress {
  stroke: var(--automation-ring-color);
  stroke-linecap: round;
}

.automation-progress-ring__tip-shadow {
  fill: rgba(255, 255, 255, 0.22);
}

.automation-progress-ring__tip {
  fill: #fff;
}

.automation-progress-ring__icon {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  color: var(--automation-ring-color);
  font-variation-settings:
    'FILL' 1,
    'wght' 500,
    'GRAD' 0,
    'opsz' 20;
}

/* The drawn set's middle (P11 sweep): same slot and colour, sizes itself. */
.automation-progress-ring__glyph {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--automation-ring-color);
}
</style>
